import { Box, Button, TextField, Tooltip, Typography } from "@mui/material";
import { useState, useMemo, useEffect, memo } from "react";
import PinIcon from "../../../assets/images/PushPinned.svg";
import FillPinIcon from "../../../assets/images/fillpin.svg";
import CopyIcon from "../../../assets/images/copyIcon.svg";
import LikeIcon from "../../../assets/images/like.svg";
import FillLikeIcon from "../../../assets/images/fillLike.svg";
import UnlikeIcon from "../../../assets/images/unlike.svg";
import FillUnlikeIcon from "../../../assets/images/fillunlike.svg";
import EditIcon from "../../../assets/images/editIcon.svg";
import RefreshIcon from "../../../assets/images/refresh.svg";
import ChatIcon from "../../../assets/images/chat.svg";
import ExportIcon from "../../../assets/images/export.svg";
import ExpandIcon from "../../../assets/images/expandmore.svg";
import { FeedbackDialog } from "./FeedbackDialog";
import {
  feedbackapiV2,
  pinnedPrompt,
  unpinnedPrompt,
} from "Samarth Module/api/samarthAiAPI";
import TableIcon from "../../../assets/images/tableIcon.svg";
import GraphIcon from "../../../assets/images/graphIcon.svg";
import ExpandLessIcon from "../../../assets/images/expandless.svg";
import ReactMarkdown from "react-markdown";
import OverallView from "./Graphs/OverallView";
import BotIcon from "../../../assets/images/botIcon.png";
import FollowupIcon from "../../../assets/images/followuparrow.svg";
// styles.js (or keep at top of file)
const COLORS = {
  primary: "#0066CC",
  primaryHover: "#005BB5",
  dark: "#1A2025",
  mid: "#4D5F6E",
  light: "#F5F7F9",
  bg: "#F6F7F0",
  bgHover: "#E8ECEF",
  border: "#1A2025",
  white: "#FFFFFF",
  inputBg: "#F6F7F0",
  iconBg: "#F8FBFC",
  iconHover: "#C2E0FF",
};

export const styles = {
  icon: {
    height: {
      xs: "16px",
      sm: "18px",
      md: "20px",
    },
    width: {
      xs: "16px",
      sm: "18px",
      md: "20px",
    },
    borderRadius: "50%",
    background: COLORS.iconBg,
    p: {
      xs: "8px",
      sm: "12px",
      md: "16px",
    },

    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { background: COLORS.iconHover },
  },
};

export const useFeedback = ({
  msg,
  conversationId,
  spaceId,
  onLiked,
  onUnliked,
  messageId,
}) => {
 const [feedback, setFeedback] = useState(msg.feedback?.toLowerCase() ?? null);
  const [openDialog, setOpenDialog] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [loading, setLoading] = useState(false);
  const submitFeedback = async ({ feedbackValue, comment, onSuccess }) => {
    const prev = feedback;
    setFeedback(feedbackValue); // optimistic
    try {
      await feedbackapiV2({
        conversation_id: conversationId,
        message_id: messageId,
        space_id: spaceId,
        feedback: feedbackValue,
        feedback_comment: comment,
      });
      onSuccess?.();
    } catch (err) {
      console.error("Feedback failed", err);
      setFeedback(prev); // rollback
    }
  };

  const handleLike = () =>
    submitFeedback({
      feedbackValue: feedback === "positive" ? null : "positive",
      comment: "",
      onSuccess: () =>
        onLiked?.(msg.id, feedback === "positive" ? null : "positive"),
    });

  const handleUnlikeSubmit = async () => {
    if (!feedbackComment.trim()) return;
    setLoading(true);
    await submitFeedback({
      feedbackValue: "negative",
      comment: feedbackComment,
      onSuccess: () => {
        onUnliked?.(msg.id, "negative");
        setOpenDialog(false);
        setFeedbackComment("");
      },
    });
    setLoading(false);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setFeedbackComment("");
  };

  return {
    feedback,
    openDialog,
    setOpenDialog,
    feedbackComment,
    setFeedbackComment,
    loading,
    handleLike,
    handleUnlikeSubmit,
    handleDialogClose,
  };
};
export const IconAction = ({
  src,
  onClick,
  height = { sm: 12, md: 16, lg: 20 },
}) => (
  <Box sx={styles.icon} onClick={onClick}>
    <Box component="img" src={src} height={height} />
  </Box>
);
const tooltipProps = {
  slotProps: {
    tooltip: {
      sx: {
        backgroundColor: "#F6F7F0 !important",
        color: "#1A2025",
        fontSize: { sm: "7px", md: "8px", lg: "12px" },
        p: "4px 16px !important",
        fontWeight: 400,
        borderRadius: "6px",
      },
    },
  },
};
export const UserActions = ({
  msg,
  pinned,
  onEditClick,
  onCopy,
  onCopyTable,
  onPinClick,
  isLastMessage,
}) => {
  const [copyTooltip, setCopyTooltip] = useState("Copy");
  const [pinTooltip, setPinTooltip] = useState(pinned ? "Unpin" : "Pin");

  const handleCopy = () => {
    if (msg.type === "table") {
      onCopyTable(msg.columns, msg.rows);
    } else {
      onCopy(msg.text || msg.content);
    }
    setCopyTooltip("Copied");
    setTimeout(() => setCopyTooltip("Copy"), 1500);
  };

  const handlePin = () => {
    onPinClick();
    setPinTooltip(pinned ? "Prompt Unpinned" : "Prompt Pinned");
    setTimeout(() => setPinTooltip(pinned ? "Pin" : "Unpin"), 1500);
  };

  return (
    <Box sx={{ display: "inline-flex", gap: 1, alignItems: "center" }}>
      {isLastMessage && (
        <Tooltip title="Edit" placement="top" {...tooltipProps}>
          <span>
            <IconAction src={EditIcon} onClick={onEditClick} />
          </span>
        </Tooltip>
      )}

      <Tooltip title={copyTooltip} placement="top" {...tooltipProps}>
        <span>
          <IconAction src={CopyIcon} onClick={handleCopy} />
        </span>
      </Tooltip>

      <Tooltip title={pinTooltip} placement="top" {...tooltipProps}>
        <span>
          <IconAction
            src={pinned ? FillPinIcon : PinIcon}
            onClick={handlePin}
          />
        </span>
      </Tooltip>
    </Box>
  );
};

export const BotActions = ({
  msg,
  feedback,
  onLike,
  onUnlike,
  onCopy,
  onCopyTable,
  onRefresh,
}) => {
  const [copyTooltip, setCopyTooltip] = useState("Copy");
  const handleCopy = () => {
    if (msg.type === "table") {
      onCopyTable(msg.columns, msg.rows);
    } else {
      onCopy(msg.text || msg.content);
    }
    setCopyTooltip("Copied");
    setTimeout(() => setCopyTooltip("Copy"), 1500);
  };
  return (
    <Box sx={{ display: "inline-flex", gap: 1, alignItems: "center" }}>
      <Tooltip title={feedback === "positive" ? "Liked" : "Like"} placement="top" {...tooltipProps}>
        <span>
          <IconAction
            src={feedback === "positive" ? FillLikeIcon : LikeIcon}
            onClick={onLike}
          />
        </span>
      </Tooltip>
      <Tooltip title={feedback === "negative" ? "Disliked" : "Dislike"} placement="top" {...tooltipProps}>
        <span>
          <IconAction
            src={feedback === "negative" ? FillUnlikeIcon : UnlikeIcon}
            onClick={onUnlike}
          />
        </span>
      </Tooltip>
      <Tooltip title={copyTooltip} placement="top" {...tooltipProps}>
        <span>
          <IconAction src={CopyIcon} onClick={handleCopy} />
        </span>
      </Tooltip>
    </Box>
  );
};
const baseTextStyle = {
  color: "#4D5F6E",
};

const headingStyle = (size) => ({
  fontSize: size,
  margin: "8px 0",
  fontWeight: 600,
});
const components = {
  h1: ({ children }) => (
    <Box
      component="h1"
      sx={{
        ...headingStyle(),
        fontSize: { sm: "16px", md: "20px", lg: "24px" },
      }}
    >
      {children}
    </Box>
  ),

  h2: ({ children }) => (
    <Box
      component="h2"
      sx={{
        ...headingStyle(),
        fontSize: { sm: "14px", md: "16px", lg: "20px" },
      }}
    >
      {children}
    </Box>
  ),

  h3: ({ children }) => (
    <Box
      component="h3"
      sx={{
        ...headingStyle(),
        fontSize: { sm: "12px", md: "14px", lg: "18px" },
      }}
    >
      {children}
    </Box>
  ),

  h4: ({ children }) => (
    <Box
      component="h4"
      sx={{
        ...headingStyle(),
        fontSize: { sm: "10px", md: "12px", lg: "16px" },
      }}
    >
      {children}
    </Box>
  ),

  h5: ({ children }) => (
    <Box
      component="h5"
      sx={{
        ...headingStyle(),
        fontSize: { sm: "9px", md: "10px", lg: "15px" },
      }}
    >
      {children}
    </Box>
  ),

  h6: ({ children }) => (
    <Box
      component="h6"
      sx={{
        ...headingStyle(),
        fontSize: { sm: "8px", md: "10px", lg: "14px" },
      }}
    >
      {children}
    </Box>
  ),

  p: ({ children }) => (
    <Box
      component="p"
      sx={{
        ...baseTextStyle,
        fontSize: { sm: "8px", md: "10px", lg: "14px" },
        my: { sm: 0.5, md: 1, lg: 1 }, // better than margin
        lineHeight: { sm: "16px", md: "20px", lg: "24px" },
      }}
    >
      {children}
    </Box>
  ),

  ul: ({ children }) => (
    <Box
      component="ul"
      sx={{
        pl: 2,
        my: { sm: 0.5, md: 1 },
      }}
    >
      {children}
    </Box>
  ),

  ol: ({ children }) => (
    <Box
      component="ol"
      sx={{
        pl: 2,
        my: { sm: 0.5, md: 1 },
      }}
    >
      {children}
    </Box>
  ),

  li: ({ children }) => (
    <Box
      component="li"
      sx={{
        fontSize: { sm: "8px", md: "10px", lg: "14px" },
        mb: { sm: 0.5, md: 1 },
      }}
    >
      {children}
    </Box>
  ),
};
export const lossByLossConfig = {
  title: "Loss by loss reasons",
  lastRefreshed: "",

  data: [],

  styles: {
    barColor: "#B6F7FF",
    valueColor: "#1A2025",
    smallBarColor: "#1A2025",
    percentColor: "#667F93",
  },
};
const MAX_VISIBLE_CHIPS = 4;

const QuotedTextPreview = memo(({ text }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1,
      mb: 1,
      pr: 1,
      color: "#94A3B8",
    }}
  >
    <Box
      component="img"
      src={FollowupIcon}
      sx={{ mt: { md: "2px", lg: "4px" } }}
    />
    <Typography
      sx={{
        fontSize: { sm: "8px", md: "10px", lg: "14px" },
        color: "#4D5F6E",
        lineHeight: 1.5,
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
        wordBreak: "break-word",
      }}
    >
      {text}
    </Typography>
  </Box>
));

const FilterChips = ({ filters }) => {
  const allValues = Object.entries(filters).flatMap(([, values]) => values);
  if (allValues.length === 0) return null;

  const visible = allValues.slice(0, MAX_VISIBLE_CHIPS);
  const remaining = allValues.length - MAX_VISIBLE_CHIPS;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
        mt: 1.5,
      }}
    >
      <Typography
        sx={{
          fontSize: { sm: "6px", md: "7px", lg: "11px" },
          fontWeight: 600,
          color: "#667F93",
          letterSpacing: "0.5px",
          mr: "4px",
        }}
      >
        FILTERS
      </Typography>
      {visible.map((value, idx) => (
        <Box
          key={idx}
          sx={{
            fontSize: { sm: "7px", md: "8px", lg: "12px" },
            fontWeight: 500,
            color: "#0066CC",
            backgroundColor: "#EAF2FF",
            borderRadius: "16px",
            padding: "4px 12px",
            whiteSpace: "nowrap",
            border: "1px solid #C2E0FF",
          }}
        >
          {value}
        </Box>
      ))}
      {remaining > 0 && (
        <Tooltip
          title={allValues.slice(MAX_VISIBLE_CHIPS).join(", ")}
          placement="bottom"
          {...tooltipProps}
        >
          <Box
            sx={{
              fontSize: { sm: "7px", md: "8px", lg: "12px" },
              fontWeight: 500,
              color: "#0066CC",
              backgroundColor: "#EAF2FF",
              borderRadius: "16px",
              padding: "4px 12px",
              whiteSpace: "nowrap",
              border: "1px solid #C2E0FF",
              cursor: "pointer",
            }}
          >
            +{remaining} More
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const MessageBubbleComponent = ({
  msg,
  editingMessageId,
  editedText,
  setEditedText,
  setEditingMessageId,
  onEditSend,
  onCopy,
  onCopyTable,
  onRefresh,
  onPinned,
  onLiked,
  onUnliked,
  conversationId,
  spaceId,
  messageId,
  lastId,
  onGraphClick,
  onGraphTypeChange,
}) => {
  const isUser = msg.sender === "user";
  const isEditing = editingMessageId === msg.id;
  const [pinned, setPinned] = useState(msg.is_pinned ?? false);
  const userInfo = useMemo(
    () => JSON.parse(localStorage.getItem("UserInfo"))?.user_info,
    [],
  );
  const [isFocused, setIsFocused] = useState(false);
  const {
    feedback,
    openDialog,
    setOpenDialog,
    feedbackComment,
    setFeedbackComment,
    loading,
    handleLike,
    handleUnlikeSubmit,
    handleDialogClose,
  } = useFeedback({
    msg,
    conversationId,
    spaceId,
    onLiked,
    onUnliked,
    messageId,
  });
  useEffect(() => {
    setPinned(msg.is_pinned ?? false);
  }, [msg.is_pinned]);

  const handlePinClick = () => {
    const payload = {
      user_id: userInfo.id,
      user_name: userInfo.name,
      user_email: userInfo.email,
      message_id: msg.id,
    };

    setPinned((prev) => {
      const newPinned = !prev;

      if (newPinned) {
        pinnedPrompt(payload); // ✅ pin
      } else {
        unpinnedPrompt(payload); // ✅ unpin
      }

      onPinned?.(msg.id);

      return newPinned;
    });
  };
  const formatLabel = (text) => {
    if (!text) return "";
    return String(text)
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };
  const { headerConfig, tableRows, colors } = useMemo(() => {
    const columns = msg?.columns ?? [];
    return {
      headerConfig: [
        {
          cells: columns
            .filter((c) => !c.parent)
            .map((c) => ({
              label: formatLabel(c.name),
              rowSpan: c.colSpan ? undefined : 2,
              colSpan: c.colSpan,
              group: c.group,
              width: c.width ?? 120,
            })),
        },
        {
          cells: columns
            .filter((c) => c.parent)
            .map((c) => ({
              label: formatLabel(c.name),
              parent: c.parent,
              width: c.width ?? 120,
            })),
        },
      ],
      tableRows: (msg?.rows ?? []).map((row, i) => ({
        id: i,
        cells: row.map((cell, ci) => ({
          value: cell ?? "-",
          width: columns[ci]?.width ?? 120,
        })),
      })),
      colors: {
        headerDark: "#354ACD",
        headerMid: "#C2E0FF",
        headerLight: "#354ACD",
        borderDark: "#D9E1E7",
        borderMid: "#4D5F6E",
        borderLight: "#D9E1E7",
        textDark: "#fff",
        textMid: "#1A2025",
        textLight: "#fff",
      },
    };
  }, [msg]);
  const rows1 = msg?.table?.rows || msg?.rows || [];
  const firstRowCells = headerConfig?.[0]?.cells;
  const columns = firstRowCells || headerConfig;
  const firstRow = rows1?.[0] || {};

  const items = columns.map((col, idx) => {
    const valueKey = col?.accessor || col?.key || col?.label;

    let value = firstRow?.[valueKey];

    if (value === undefined) {
      value = firstRow?.cells?.[idx]?.value;
    }

    return {
      title: col?.label || col?.header || "KPI",
      value: value ?? "-",
      year: firstRow?.year ?? "",
    };
  });

  // 👇 get title
  const title = items?.[0]?.title;
  return (
    <>
      <Box
        display="flex"
        justifyContent={isUser ? "flex-end" : "flex-start"}
        mb={2}
      >
        <Box
          sx={{
            width: isUser ? { xs: "100%", sm: "80%", md: "60%" } : "100%",
            display: "flex",
            flexDirection: "column",
            "&:hover .message-actions": { opacity: 1 },
          }}
        >
          {/* Bubble */}
          {msg.sender === "bot" && msg.id === lastId && (
            <>
              <Box
                component="img"
                src={BotIcon}
                alt="Info"
                sx={{ width: 80, height: 80 }}
              />
            </>
          )}
          {isUser && msg.quotedText && (
            <QuotedTextPreview text={msg.quotedText} />
          )}
          <Box
            sx={{
              wordBreak: "break-word",
              padding: isUser
                ? {
                    sm: "12px 14px",
                    md: "16px 18px",
                    lg: "20px 24px",
                  }
                : "",
              borderRadius:
                isEditing && msg.sender === "user"
                  ? {
                      sm: "16px",
                      md: "20px",
                      lg: "24px",
                    }
                  : msg.sender === "user"
                    ? {
                        sm: "16px 0px 16px 16px",
                        md: "20px 0px 20px 20px",
                        lg: "24px 0px 24px 24px",
                      }
                    : {
                        sm: "0px 16px 16px 16px",
                        md: "0px 20px 20px 20px",
                        lg: "0px 24px 24px 24px",
                      },
              background: isUser ? "#E0EFFF" : "transparent",
              color: isUser ? "#1A2025" : "#4D5F6E",
              boxShadow: isUser ? "0 1px 4px rgba(0,0,0,0.1)" : "",
              border: isUser && isEditing ? "1px solid #0066CC" : "",
            }}
          >
            {isUser && isEditing ? (
              <Box sx={{ width: "100%" }}>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={10}
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    wordBreak: "break-word",
                    overflow: "hidden",

                    "& .MuiOutlinedInput-root": {
                      padding: 0,
                      color: "#1A2025",
                      "&:hover, &.Mui-focused": {
                        backgroundColor: "transparent",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiInputBase-input": {
                      padding: 0,
                      fontSize: { sm: "8px", md: "10px", lg: "14px" },
                      wordBreak: "break-word",
                      color: "#1A2025",
                    },
                  }}
                />
                <Box display="flex" gap={1} justifyContent="flex-end">
                  <Button
                    size="small"
                    onClick={() => {
                      setEditingMessageId(null);
                      setEditedText("");
                    }}
                    sx={{
                      background: "transparent",
                      borderRadius: "29px",
                      padding: "8px 18px",
                      border: "none",
                      color: "#4D5F6E",
                      fontSize: { sm: "8px", md: "10px", lg: "14px" },
                      fontWeight: 400,
                      textTransform: "capitalize",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onEditSend(msg.id)}
                    disabled={
                      !isFocused &&
                      (!editedText.trim() || editedText === msg.text)
                    }
                    sx={{
                      background: "#F6F7F0",
                      borderRadius: "29px",
                      padding: "8px 18px",
                      color: "#0066CC",
                      fontSize: { sm: "8px", md: "10px", lg: "14px" },
                      fontWeight: 400,
                      boxShadow: "none",
                      "&:hover": {
                        background: "#F6F7F0",
                        boxShadow: "none",
                      },
                      textTransform: "capitalize",
                      "&.Mui-disabled": {
                        opacity: 0.5,
                        color: "#999",
                        background: "#eee",
                      },
                    }}
                  >
                    Send
                  </Button>
                </Box>
              </Box>
            ) : msg.type === "table" ? (
              <>
                <Typography
                  component="div"
                  sx={{
                    fontSize: { sm: "8px", md: "10px", lg: "14px" },
                    color: "#4D5F6E",
                    mb: { md: 2, lg: 4 },
                  }}
                >
                  <ReactMarkdown components={components}>
                    {msg.text || msg.content}
                  </ReactMarkdown>
                </Typography>
                <OverallView
                  title={firstRowCells?.length === 1 ? title : "Order Overview"}
                  refreshTime="Updated 2 mins ago"
                  data={lossByLossConfig.data}
                  {...lossByLossConfig.styles}
                  icons={{
                    refresh: RefreshIcon,
                    chat: ChatIcon,
                    export: ExportIcon,
                    expand: ExpandIcon,
                    table: TableIcon,
                    graph: GraphIcon,
                    expandless: ExpandLessIcon,
                  }}
                  showRefresh={false}
                  showChat={false}
                  headerConfig={headerConfig}
                  rows={tableRows}
                  colors={colors}
                  showViewOptions={msg.showGraph}
                  // showViewOptions={false}
                  onGraphClick={onGraphClick}
                  onGraphTypeChange={
                    onGraphTypeChange
                      ? (newType) => onGraphTypeChange(msg.id, newType)
                      : undefined
                  }
                  msg={msg}
                  messageId={messageId}
                  conversationId={conversationId}
                />
              </>
            ) : msg.sender === "bot" ? (
              <ReactMarkdown components={components}>
                {msg.text || msg.content}
              </ReactMarkdown>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: { sm: "8px", md: "10px", lg: "14px" },
                    color: "#1A2025",
                  }}
                >
                  {msg.text || msg.content}
                </Typography>
              </>
            )}
          </Box>
          {msg.sender === "user" &&
            msg.filters &&
            Object.keys(msg.filters).length > 0 && (
              <FilterChips filters={msg.filters} />
            )}

          {/* Actions */}
          <Box
            className="message-actions"
            sx={{
              display: "flex",
              gap: 1,
              mt: 1,
              opacity: 0,
              transition: "opacity 0.2s ease",
              justifyContent: isUser ? "flex-end" : "flex-start",
            }}
          >
            {isUser ? (
              <UserActions
                msg={msg}
                pinned={pinned}
                isLastMessage={msg.id === lastId}
                onEditClick={() => {
                  setEditingMessageId(msg.id);
                  setEditedText(msg.text);
                }}
                onCopy={onCopy}
                onCopyTable={onCopyTable}
                onPinClick={handlePinClick}
              />
            ) : (
              <BotActions
                msg={msg}
                feedback={feedback}
                onLike={handleLike}
                onUnlike={() => setOpenDialog(true)}
                onCopy={onCopy}
                onCopyTable={onCopyTable}
                onRefresh={onRefresh}
              />
            )}
          </Box>
        </Box>
      </Box>

      <FeedbackDialog
        open={openDialog}
        onClose={handleDialogClose}
        title="Share Feedback"
        subTitle="What could have been better?"
        feedbackComment={feedbackComment}
        setFeedbackComment={setFeedbackComment}
        onSubmit={handleUnlikeSubmit}
        loading={loading}
        buttonLabel="Send"
        minRows={3}
        placeholder="Enter feedback..."
      />
    </>
  );
};;

export const MessageBubble = memo(MessageBubbleComponent, (prev, next) => {
  // Re-render only when meaningful props change
  return (
    prev.msg === next.msg &&
    prev.editingMessageId === next.editingMessageId &&
    prev.editedText === next.editedText &&
    prev.lastId === next.lastId &&
    prev.conversationId === next.conversationId &&
    prev.spaceId === next.spaceId
  );
});